/**
 * Core API client — centralized fetch wrapper with:
 * - Timeout control
 * - Automatic retry with exponential backoff
 * - Global error normalization
 * - Request deduplication (in-flight map)
 * - JSON parsing
 */

/**
 * Rutas públicas de ChekList (catálogo y fotos), que lee la hoja de Google Sheets.
 * VITE_CHECKLIST_URL es el dominio de ChekList, p. ej. https://gestion.onfase.com
 */
const CHECKLIST_URL = (import.meta.env.VITE_CHECKLIST_URL ?? '').replace(/\/+$/, '');
export const API_BASE_URL = `${CHECKLIST_URL}/api/publico`;
export const CATALOGO_URL = `${API_BASE_URL}/catalogo`;

// ─── Config ───────────────────────────────────────────────────────────────────
const DEFAULT_TIMEOUT_MS = 10_000; // 10 seconds
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;

// ─── Custom API Error ─────────────────────────────────────────────────────────
export class ApiError extends Error {
  public readonly status: number;
  public readonly retryable: boolean;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    // 5xx errors and network errors are retryable; 4xx are not
    this.retryable = status === 0 || status >= 500;
  }
}

// ─── Request options ──────────────────────────────────────────────────────────
export interface RequestOptions {
  params?: Record<string, string | number | boolean | undefined | null>;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: string;
  timeoutMs?: number;
  retries?: number;
  signal?: AbortSignal;
}

// ─── In-flight deduplication map ─────────────────────────────────────────────
const inFlight = new Map<string, Promise<unknown>>();

// ─── Build URL with query params ──────────────────────────────────────────────
export function buildUrl(
  endpoint: string,
  params?: RequestOptions['params'],
): string {
  const base = /^https?:\/\//.test(endpoint) || endpoint.startsWith('/')
    ? endpoint
    : `${API_BASE_URL}/${endpoint}`;
  const url = new URL(base, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.toString();
}

// ─── Sleep helper ─────────────────────────────────────────────────────────────
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ─── Single fetch attempt with timeout ───────────────────────────────────────
async function fetchWithTimeout<T>(
  url: string,
  options: RequestOptions,
): Promise<T> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();

  // Merge external signal if provided
  const externalSignal = options.signal;
  if (externalSignal) {
    externalSignal.addEventListener('abort', () => controller.abort());
  }

  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: options.method ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...options.headers,
      },
      body: options.body,
      signal: controller.signal,
      cache: 'no-store',
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new ApiError(
        response.status,
        `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    const json = await response.json();
    return json as T;
  } catch (err) {
    clearTimeout(timeoutId);

    if (err instanceof ApiError) throw err;

    // AbortError from timeout
    if (err instanceof Error && err.name === 'AbortError') {
      throw new ApiError(0, `Request timed out after ${timeoutMs}ms`);
    }

    // Network error
    throw new ApiError(0, 'Error de red. Verifica tu conexión a internet.');
  }
}

// ─── Core request with retry ──────────────────────────────────────────────────
async function requestWithRetry<T>(
  url: string,
  options: RequestOptions,
  attempt = 0,
): Promise<T> {
  try {
    return await fetchWithTimeout<T>(url, options);
  } catch (err) {
    const maxRetries = options.retries ?? MAX_RETRIES;
    const isRetryable = err instanceof ApiError && err.retryable;

    if (isRetryable && attempt < maxRetries) {
      const delay = RETRY_DELAY_MS * Math.pow(2, attempt); // exponential backoff
      await sleep(delay);
      return requestWithRetry<T>(url, options, attempt + 1);
    }

    throw err;
  }
}

// ─── Public request function ──────────────────────────────────────────────────
export async function request<T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const url = buildUrl(endpoint, options.params);
  const method = options.method ?? 'GET';

  // Only deduplicate GET requests
  if (method === 'GET') {
    const key = url;
    const existing = inFlight.get(key);
    if (existing) return existing as Promise<T>;

    const promise = requestWithRetry<T>(url, options).finally(() => {
      inFlight.delete(key);
    });

    inFlight.set(key, promise);
    return promise;
  }

  return requestWithRetry<T>(url, options);
}

// ─── HTTP method helpers ──────────────────────────────────────────────────────
export const api = {
  get: <T>(endpoint: string, params?: RequestOptions['params'], opts?: Omit<RequestOptions, 'method' | 'params'>) =>
    request<T>(endpoint, { method: 'GET', params, ...opts }),

  post: <T>(endpoint: string, body: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { method: 'POST', body: JSON.stringify(body), ...opts }),

  put: <T>(endpoint: string, body: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { method: 'PUT', body: JSON.stringify(body), ...opts }),

  patch: <T>(endpoint: string, body: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { method: 'PATCH', body: JSON.stringify(body), ...opts }),

  delete: <T>(endpoint: string, opts?: Omit<RequestOptions, 'method'>) =>
    request<T>(endpoint, { method: 'DELETE', ...opts }),
};
